import { arrayMove } from '@dnd-kit/sortable';
import { CatalogCategoryOut, CatalogItemOut, PartialLabelData } from '../api';
import { localizedLabel } from '../utils/maskUtils';

export enum CatalogItemDrawerType {
  CREATE = 'CREATE',
  EDIT = 'EDIT',
  CLONE = 'CLONE',
}

export type CatalogItemFormValues = {
  category_id: string;
  name: PartialLabelData;
  description: PartialLabelData;
  photo_url?: string;
  price: number | '';
  tags: string[];
};

export const trimLabel = (label: PartialLabelData): PartialLabelData => ({
  en: label.en?.trim() || undefined,
  he: label.he?.trim() || undefined,
});

export const labelHasValue = (label?: PartialLabelData): boolean => !!(label?.en?.trim() || label?.he?.trim());

// A clone sits right next to its source in the items list, where nothing but the name tells the
// two apart -- so the suffix goes on every language the source actually filled in.
export const suffixedLabel = (label: PartialLabelData | undefined, suffix: string): PartialLabelData => ({
  en: label?.en?.trim() ? `${label.en.trim()} ${suffix}` : undefined,
  he: label?.he?.trim() ? `${label.he.trim()} ${suffix}` : undefined,
});

export const buildItemPayload = (values: CatalogItemFormValues) => {
  const description = trimLabel(values.description);
  return {
    category_id: values.category_id,
    name: trimLabel(values.name),
    description: labelHasValue(description) ? description : undefined,
    photo_url: values.photo_url || undefined,
    price: Number(values.price),
    tags: Array.from(new Set(values.tags.map((tag) => tag.trim()).filter(Boolean))),
  };
};

// Soft scoping for the add-item picker: a category with no order_page_ids belongs everywhere,
// otherwise it shows only on its own pages. `showAll` is the escape hatch, so a scoping gap
// never dead-ends staff mid-shift.
export const visibleCatalogCategories = (
  categories: CatalogCategoryOut[],
  orderPageId?: string,
  showAll = false,
  language?: string,
): CatalogCategoryOut[] =>
  categories
    .filter((category) => !category.archived)
    .filter(
      (category) =>
        showAll || !category.order_page_ids?.length || (!!orderPageId && category.order_page_ids.includes(orderPageId)),
    )
    .sort((first, second) =>
      language
        ? localizedLabel(first.label, language).localeCompare(localizedLabel(second.label, language), language)
        : first.sort_order - second.sort_order,
    );

export type CatalogPickerFilters = {
  categoryIds: string[];
  tags: string[];
  search: string;
};

// The picker filters in the browser rather than through the API on purpose: the whole active
// catalog is already cached, so filtering stays instant while staff type on a phone, and the
// order-page scoping below has no server-side equivalent.
export const filterCatalogItemsForPicker = (
  items: CatalogItemOut[],
  visibleCategories: CatalogCategoryOut[],
  filters: CatalogPickerFilters,
  language: string,
): CatalogItemOut[] => {
  const visibleCategoryIds = new Set(visibleCategories.map((category) => category.id));
  const searchValue = filters.search.trim().toLowerCase();
  return items
    .filter((item) => visibleCategoryIds.has(item.category_id))
    .filter((item) => !filters.categoryIds.length || filters.categoryIds.includes(item.category_id))
    .filter((item) => !filters.tags.length || (item.tags ?? []).some((tag) => filters.tags.includes(tag)))
    .filter(
      (item) =>
        !searchValue ||
        localizedLabel(item.name, language).toLowerCase().includes(searchValue) ||
        (item.tags ?? []).some((tag) => tag.toLowerCase().includes(searchValue)),
    );
};

// An order page gets its own tab in the add-item picker only when a category with items is assigned to it.
export const pickerOrderPageIds = (
  items: CatalogItemOut[],
  categories: CatalogCategoryOut[],
  orderPageIds: string[],
): string[] => {
  const categoryIdsWithItems = new Set(items.map((item) => item.category_id));
  const assignedCategories = categories.filter(
    (category) => !category.archived && categoryIdsWithItems.has(category.id),
  );
  return orderPageIds.filter((pageId) =>
    assignedCategories.some((category) => category.order_page_ids?.includes(pageId)),
  );
};

// Opens on the order's own page when it has a tab, else the first page that has one.
export const defaultPickerOrderPageId = (pageIds: string[], orderPageId?: string): string | undefined =>
  orderPageId && pageIds.includes(orderPageId) ? orderPageId : pageIds[0];

export const collectExistingTags = (items: CatalogItemOut[]): string[] =>
  Array.from(new Set(items.flatMap((item) => item.tags ?? []))).sort((a, b) => a.localeCompare(b));

export const applyCategoryReorder = (
  visibleCategories: CatalogCategoryOut[],
  activeId: string,
  overId: string,
): { reordered: CatalogCategoryOut[]; changes: CatalogCategoryOut[] } => {
  const oldIndex = visibleCategories.findIndex((category) => category.id === activeId);
  const newIndex = visibleCategories.findIndex((category) => category.id === overId);
  if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) {
    return { reordered: visibleCategories, changes: [] };
  }
  const reordered = arrayMove(visibleCategories, oldIndex, newIndex).map((category, index) => ({
    ...category,
    sort_order: index,
  }));
  const previousSortOrderById = new Map(visibleCategories.map((category) => [category.id, category.sort_order]));
  const changes = reordered.filter((category) => previousSortOrderById.get(category.id) !== category.sort_order);
  return { reordered, changes };
};
